import './Campaigns.css';
import api from '../api.js';
import { useEffect, useMemo, useState } from 'react';
import plus from '../imgs/plus.svg';
import {
    buildRecipientsFromSheet,
    downloadRecipientsTemplate,
    guessPhoneColumnIndex,
    guessVariableColumnIndices,
    parseRecipientsText,
    readSpreadsheetFile,
} from '../utils/campaignRecipients.js';

const STATUS_CLASS = {
    'Черновик': 'draft',
    'В процессе': 'running',
    'Завершено': 'done',
    'Ошибка': 'failed',
};

const EMPTY_FORM = {
    partnerName: '',
    name: '',
    templateId: '',
    mediaUrl: '',
    recipientsText: '',
    startImmediately: true,
};

const EMPTY_EXCEL = {
    fileName: '',
    headers: [],
    rows: [],
    phoneColumnIndex: 0,
    variableColumnIndices: [],
};

export default function Campaigns() {
    const me = JSON.parse(localStorage.getItem('me') || '{}');
    const role = me?.userInfo?.role;
    const myPartnerName = me?.userInfo?.partnerName;

    const [campaigns, setCampaigns] = useState([]);
    const [templates, setTemplates] = useState([]);
    const [partners, setPartners] = useState([]);
    const [selectedPartner, setSelectedPartner] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [search, setSearch] = useState('');
    const [loading, setLoading] = useState(true);
    const [isCreateOpen, setIsCreateOpen] = useState(false);
    const [processing, setProcessing] = useState(false);
    const [startingId, setStartingId] = useState(null);
    const [error, setError] = useState('');
    const [message, setMessage] = useState('');
    const [form, setForm] = useState(EMPTY_FORM);
    const [recipientMode, setRecipientMode] = useState('excel');
    const [excelData, setExcelData] = useState(EMPTY_EXCEL);
    const [excelLoading, setExcelLoading] = useState(false);

    const loadCampaigns = async (partnerFilter = selectedPartner) => {
        setLoading(true);
        setError('');

        try {
            const params = {};
            if (role === 'Админ' && partnerFilter) {
                params.partnerName = partnerFilter;
            }

            const res = await api.get('/api/campaigns/list', { params });
            setCampaigns(res.data);
        } catch (err) {
            setError(err?.response?.data || 'Ошибка при загрузке рассылок');
            setCampaigns([]);
        } finally {
            setLoading(false);
        }
    };

    const loadTemplates = async (partnerName) => {
        try {
            const params = {};
            if (role === 'Админ' && partnerName) {
                params.partnerName = partnerName;
            }
            const res = await api.get('/api/templates/list', { params });
            setTemplates(res.data.filter((t) => t.isActive !== false));
        } catch {
            setTemplates([]);
        }
    };

    useEffect(() => {
        document.title = 'Рассылки - WAve';

        if (role === 'Админ') {
            api.get('/api/partners/list')
                .then((res) => {
                    setPartners(res.data);
                    if (res.data.length > 0) {
                        setForm((f) => ({ ...f, partnerName: res.data[0].name }));
                        loadTemplates(res.data[0].name);
                    }
                })
                .catch(() => {});
        } else if (myPartnerName) {
            setForm((f) => ({ ...f, partnerName: myPartnerName }));
            loadTemplates(myPartnerName);
        }

        loadCampaigns();
    }, []);

    useEffect(() => {
        if (!loading) {
            loadCampaigns(selectedPartner);
        }
    }, [selectedPartner]);

    const selectedTemplate = useMemo(
        () => templates.find((t) => String(t.id) === String(form.templateId)),
        [templates, form.templateId]
    );

    const variableCount = selectedTemplate?.variableCount ?? 0;

    const excelPreviewRecipients = useMemo(() => {
        if (!excelData.rows.length) return [];

        try {
            return buildRecipientsFromSheet(
                excelData.rows,
                excelData.phoneColumnIndex,
                excelData.variableColumnIndices,
                variableCount
            );
        } catch {
            return [];
        }
    }, [excelData, variableCount]);

    const filteredCampaigns = useMemo(() => {
        return campaigns.filter((c) => {
            const matchesSearch = !search || c.name.toLowerCase().includes(search.toLowerCase());
            const matchesStatus = !statusFilter || c.status === statusFilter;
            return matchesSearch && matchesStatus;
        });
    }, [campaigns, search, statusFilter]);

    const summary = useMemo(() => ({
        total: campaigns.length,
        running: campaigns.filter((c) => c.status === 'В процессе').length,
        completed: campaigns.filter((c) => c.status === 'Завершено').length,
        draft: campaigns.filter((c) => c.status === 'Черновик').length,
    }), [campaigns]);

    const resetExcelData = () => setExcelData(EMPTY_EXCEL);

    const openCreate = () => {
        setError('');
        setMessage('');
        setRecipientMode('excel');
        resetExcelData();
        setForm({
            ...EMPTY_FORM,
            partnerName: role === 'Партнер'
                ? (myPartnerName || '')
                : (partners[0]?.name || ''),
        });
        if (role === 'Админ' && partners[0]?.name) {
            loadTemplates(partners[0].name);
        }
        setIsCreateOpen(true);
    };

    const closeCreate = () => {
        setIsCreateOpen(false);
        setError('');
        setMessage('');
        resetExcelData();
    };

    const handlePartnerChange = (partnerName) => {
        setForm((f) => ({ ...f, partnerName, templateId: '' }));
        resetExcelData();
        loadTemplates(partnerName);
    };

    const handleTemplateChange = (templateId) => {
        setForm((f) => ({ ...f, templateId }));
        resetExcelData();
    };

    const handleExcelUpload = async (event) => {
        const file = event.target.files?.[0];
        event.target.value = '';

        if (!file) return;

        if (!selectedTemplate) {
            setError('Сначала выберите шаблон');
            return;
        }

        setExcelLoading(true);
        setError('');

        try {
            const parsed = await readSpreadsheetFile(file);
            const phoneColumnIndex = guessPhoneColumnIndex(parsed.headers);
            const variableColumnIndices = guessVariableColumnIndices(
                parsed.headers,
                phoneColumnIndex,
                variableCount
            );

            setExcelData({
                fileName: file.name,
                headers: parsed.headers,
                rows: parsed.rows,
                phoneColumnIndex,
                variableColumnIndices,
            });
            setMessage(`Загружено ${parsed.rows.length} строк из «${file.name}»`);
        } catch (err) {
            setError(err.message || 'Ошибка чтения файла');
            resetExcelData();
        } finally {
            setExcelLoading(false);
        }
    };

    const updatePhoneColumn = (value) => {
        const phoneColumnIndex = Number(value);
        setExcelData((prev) => ({
            ...prev,
            phoneColumnIndex,
            variableColumnIndices: guessVariableColumnIndices(
                prev.headers,
                phoneColumnIndex,
                variableCount
            ),
        }));
    };

    const updateVariableColumn = (variableIndex, columnValue) => {
        setExcelData((prev) => {
            const next = [...prev.variableColumnIndices];
            next[variableIndex] = Number(columnValue);
            return { ...prev, variableColumnIndices: next };
        });
    };

    const getRecipientsForSubmit = () => {
        if (recipientMode === 'excel') {
            if (!excelData.rows.length) {
                throw new Error('Загрузите Excel-файл с получателями');
            }

            return buildRecipientsFromSheet(
                excelData.rows,
                excelData.phoneColumnIndex,
                excelData.variableColumnIndices,
                variableCount
            );
        }

        return parseRecipientsText(form.recipientsText, variableCount);
    };

    const handleCreate = async (e) => {
        e.preventDefault();
        setProcessing(true);
        setError('');
        setMessage('');

        try {
            if (!form.templateId) {
                setError('Выберите шаблон');
                setProcessing(false);
                return;
            }

            const recipients = getRecipientsForSubmit();

            const payload = {
                partnerName: form.partnerName,
                name: form.name.trim(),
                templateId: Number(form.templateId),
                mediaUrl: selectedTemplate?.isMedia ? form.mediaUrl.trim() : null,
                startImmediately: form.startImmediately,
                recipients,
            };

            await api.post('/api/campaigns/create', payload);
            await loadCampaigns();
            closeCreate();
            setMessage(form.startImmediately
                ? `Рассылка создана и запущена (${recipients.length} получателей)`
                : `Рассылка сохранена как черновик (${recipients.length} получателей)`);
        } catch (err) {
            if (err.message && !err.response) {
                setError(err.message);
            } else if (err?.response?.status === 403) {
                setError('Отсутствует право на создание рассылок');
            } else {
                setError(err?.response?.data || 'Ошибка при создании рассылки');
            }
        } finally {
            setProcessing(false);
        }
    };

    const handleStart = async (campaignId) => {
        setStartingId(campaignId);
        setError('');
        setMessage('');

        try {
            await api.post('/api/campaigns/start', null, { params: { campaignId } });
            await loadCampaigns();
            setMessage('Рассылка запущена');
        } catch (err) {
            setError(err?.response?.data || 'Ошибка при запуске рассылки');
        } finally {
            setStartingId(null);
        }
    };

    const previewRows = excelData.rows.slice(0, 5);

    return (
        <div className="campaigns-main">
            <div className="campaigns-top">
                <div>
                    <h1>Рассылки WhatsApp</h1>
                    <p className="campaigns-subtitle">
                        Загрузите Excel с номерами и переменными — как в Sendy. Или введите получателей вручную.
                    </p>
                </div>
                <button type="button" className="createCampaign" onClick={openCreate}>
                    <span>Создать рассылку</span>
                    <img src={plus} alt="" />
                </button>
            </div>

            <div className="campaigns-toolbar">
                <div className="campaigns-filters">
                    <input
                        type="text"
                        placeholder="Поиск по названию"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                    <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                        <option value="">Все статусы</option>
                        <option value="Черновик">Черновик</option>
                        <option value="В процессе">В процессе</option>
                        <option value="Завершено">Завершено</option>
                        <option value="Ошибка">Ошибка</option>
                    </select>
                    {role === 'Админ' && partners.length > 0 && (
                        <select
                            value={selectedPartner}
                            onChange={(e) => setSelectedPartner(e.target.value)}
                        >
                            <option value="">Все партнёры</option>
                            {partners.map((p) => (
                                <option key={p.id} value={p.name}>{p.name}</option>
                            ))}
                        </select>
                    )}
                </div>
            </div>

            <div className="campaigns-summary">
                <div className="summary-card">
                    <span className="summary-label">Всего</span>
                    <span className="summary-value">{summary.total}</span>
                </div>
                <div className="summary-card">
                    <span className="summary-label">В процессе</span>
                    <span className="summary-value summary-running">{summary.running}</span>
                </div>
                <div className="summary-card">
                    <span className="summary-label">Завершено</span>
                    <span className="summary-value summary-done">{summary.completed}</span>
                </div>
                <div className="summary-card">
                    <span className="summary-label">Черновики</span>
                    <span className="summary-value summary-draft">{summary.draft}</span>
                </div>
            </div>

            {error && !isCreateOpen && <p className="error">{error}</p>}
            {message && !isCreateOpen && <p className="success">{message}</p>}

            <div className="campaigns-table-wrapper">
                <table className="campaigns-table">
                    <thead>
                        <tr>
                            <th>Название</th>
                            <th>Партнёр</th>
                            <th>Шаблон</th>
                            <th>Получателей</th>
                            <th>Отправлено</th>
                            <th>Ошибки</th>
                            <th>Статус</th>
                            <th>Дата</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr>
                                <td colSpan={9}>
                                    <span className="table-message">Загрузка...</span>
                                </td>
                            </tr>
                        ) : filteredCampaigns.length === 0 ? (
                            <tr>
                                <td colSpan={9}>
                                    <span className="table-message">Рассылок пока нет</span>
                                </td>
                            </tr>
                        ) : (
                            filteredCampaigns.map((c) => (
                                <tr key={c.id}>
                                    <td>{c.name}</td>
                                    <td>{c.partner}</td>
                                    <td>{c.templateName}</td>
                                    <td>{c.recipientCount}</td>
                                    <td>{c.sentCount}</td>
                                    <td>{c.errorCount}</td>
                                    <td>
                                        <span className={`status-badge status-${STATUS_CLASS[c.status] || 'draft'}`}>
                                            {c.status}
                                        </span>
                                    </td>
                                    <td>{new Date(c.createdAt).toLocaleString('ru-RU')}</td>
                                    <td>
                                        {c.status === 'Черновик' && (
                                            <button
                                                type="button"
                                                className="startCampaignBtn"
                                                disabled={startingId === c.id}
                                                onClick={() => handleStart(c.id)}
                                            >
                                                {startingId === c.id ? '...' : 'Запустить'}
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {isCreateOpen && (
                <div className="modalOverlay">
                    <div className="modalCampaigns">
                        <form onSubmit={handleCreate}>
                            <div className="modalTop">
                                <h3>Создать рассылку</h3>
                                <button type="button" onClick={closeCreate} aria-label="Закрыть" />
                            </div>

                            {role === 'Админ' && (
                                <div className="field">
                                    <label>Партнёр *</label>
                                    <select
                                        required
                                        value={form.partnerName}
                                        onChange={(e) => handlePartnerChange(e.target.value)}
                                    >
                                        {partners.map((p) => (
                                            <option key={p.id} value={p.name}>{p.name}</option>
                                        ))}
                                    </select>
                                </div>
                            )}

                            <div className="field">
                                <label>Название рассылки *</label>
                                <input
                                    required
                                    placeholder="Напоминание о записи — март"
                                    value={form.name}
                                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                                />
                            </div>

                            <div className="field">
                                <label>Шаблон *</label>
                                <select
                                    required
                                    value={form.templateId}
                                    onChange={(e) => handleTemplateChange(e.target.value)}
                                >
                                    <option value="">Выберите шаблон</option>
                                    {templates.map((t) => (
                                        <option key={t.id} value={t.id}>
                                            {t.name} ({t.gupshupTemplateId})
                                        </option>
                                    ))}
                                </select>
                                {templates.length === 0 && (
                                    <div className="field-hint">
                                        Нет шаблонов — сначала добавьте в разделе «Шаблоны»
                                    </div>
                                )}
                            </div>

                            {selectedTemplate?.isMedia && (
                                <div className="field">
                                    <label>Ссылка на изображение *</label>
                                    <input
                                        required
                                        placeholder="https://example.com/image.jpg"
                                        value={form.mediaUrl}
                                        onChange={(e) => setForm({ ...form, mediaUrl: e.target.value })}
                                    />
                                </div>
                            )}

                            <div className="field">
                                <label>Получатели *</label>
                                <div className="recipient-mode-tabs">
                                    <button
                                        type="button"
                                        className={recipientMode === 'excel' ? 'active' : ''}
                                        onClick={() => setRecipientMode('excel')}
                                    >
                                        Excel / CSV
                                    </button>
                                    <button
                                        type="button"
                                        className={recipientMode === 'manual' ? 'active' : ''}
                                        onClick={() => setRecipientMode('manual')}
                                    >
                                        Вручную
                                    </button>
                                </div>

                                {recipientMode === 'excel' ? (
                                    <div className="excel-upload-block">
                                        <div className="excel-actions">
                                            <label className="excel-upload-btn">
                                                {excelLoading ? 'Чтение файла...' : 'Загрузить файл'}
                                                <input
                                                    type="file"
                                                    accept=".xlsx,.xls,.csv"
                                                    disabled={!selectedTemplate || excelLoading}
                                                    onChange={handleExcelUpload}
                                                />
                                            </label>
                                            <button
                                                type="button"
                                                className="excel-template-btn"
                                                disabled={!selectedTemplate}
                                                onClick={() => downloadRecipientsTemplate(variableCount)}
                                            >
                                                Скачать шаблон Excel
                                            </button>
                                        </div>

                                        <div className="field-hint">
                                            Первая строка — заголовки. Колонка «Телефон» + {variableCount} переменных.
                                            {excelData.fileName && (
                                                <span className="excel-file-name"> Файл: {excelData.fileName}</span>
                                            )}
                                        </div>

                                        {excelData.headers.length > 0 && (
                                            <>
                                                <div className="column-mapping">
                                                    <div className="mapping-item">
                                                        <label>Колонка с телефоном</label>
                                                        <select
                                                            value={excelData.phoneColumnIndex}
                                                            onChange={(e) => updatePhoneColumn(e.target.value)}
                                                        >
                                                            {excelData.headers.map((header, index) => (
                                                                <option key={header + index} value={index}>
                                                                    {header}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    </div>

                                                    {Array.from({ length: variableCount }).map((_, index) => (
                                                        <div className="mapping-item" key={`var-${index}`}>
                                                            <label>Переменная {index + 1}</label>
                                                            <select
                                                                value={excelData.variableColumnIndices[index] ?? ''}
                                                                onChange={(e) => updateVariableColumn(index, e.target.value)}
                                                            >
                                                                <option value="">Не выбрано</option>
                                                                {excelData.headers.map((header, headerIndex) => (
                                                                    <option
                                                                        key={header + headerIndex}
                                                                        value={headerIndex}
                                                                        disabled={headerIndex === excelData.phoneColumnIndex}
                                                                    >
                                                                        {header}
                                                                    </option>
                                                                ))}
                                                            </select>
                                                        </div>
                                                    ))}
                                                </div>

                                                <div className="excel-preview">
                                                    <div className="excel-preview-top">
                                                        <span>Предпросмотр</span>
                                                        <span>
                                                            {excelPreviewRecipients.length > 0
                                                                ? `${excelPreviewRecipients.length} валидных из ${excelData.rows.length}`
                                                                : 'Проверьте сопоставление колонок'}
                                                        </span>
                                                    </div>
                                                    <div className="excel-preview-table-wrapper">
                                                        <table>
                                                            <thead>
                                                                <tr>
                                                                    <th>Телефон</th>
                                                                    {Array.from({ length: variableCount }).map((_, i) => (
                                                                        <th key={i}>Перем. {i + 1}</th>
                                                                    ))}
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {previewRows.map((row, rowIndex) => (
                                                                    <tr key={rowIndex}>
                                                                        <td>{row[excelData.phoneColumnIndex]}</td>
                                                                        {excelData.variableColumnIndices.map((colIndex, i) => (
                                                                            <td key={i}>{row[colIndex]}</td>
                                                                        ))}
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                ) : (
                                    <>
                                        <textarea
                                            required
                                            rows={6}
                                            placeholder={'996555123456;Иван;15 июня\n996555987654;Айгуль;16 июня'}
                                            value={form.recipientsText}
                                            onChange={(e) => setForm({ ...form, recipientsText: e.target.value })}
                                        />
                                        <div className="field-hint">
                                            {selectedTemplate
                                                ? `По одной строке: номер${variableCount > 0
                                                    ? `;${Array.from({ length: variableCount }, (_, i) => `перем${i + 1}`).join(';')}`
                                                    : ''}`
                                                : 'Сначала выберите шаблон'}
                                        </div>
                                    </>
                                )}
                            </div>

                            <label className="checkbox-row">
                                <input
                                    type="checkbox"
                                    checked={form.startImmediately}
                                    onChange={(e) => setForm({ ...form, startImmediately: e.target.checked })}
                                />
                                Запустить сразу после создания
                            </label>

                            {error && <p className="error">{error}</p>}
                            {message && isCreateOpen && <p className="success">{message}</p>}

                            <div className="buttonsModal">
                                <button
                                    type="submit"
                                    className="saveChangesPartners"
                                    disabled={processing || excelLoading}
                                >
                                    {processing ? 'Сохранение...' : 'Создать'}
                                </button>
                                <button
                                    type="button"
                                    className="cancelPartners"
                                    onClick={closeCreate}
                                    disabled={processing}
                                >
                                    Отмена
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
